import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CaretDown, Buildings, Plus, Check, Lock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sidebar dropdown that shows the active brand + lets the user switch or
 * jump to /settings/brands to manage them. Free/Pro users see a Lock badge
 * on the "Add brand" CTA — clicking it deep-links to /pricing.
 *
 * Emits a global window event 'brand:changed' when the active brand changes
 * so page-level components can refetch their data.
 */
export default function BrandSwitcher() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState({ brands: [], active_brand_id: null, is_agency: false, max_brands: 1 });
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/brands`, { withCredentials: true });
      setData(res.data);
    } catch {
      // Silent — user might be pre-auth or backend transient
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const activate = useCallback(async (brandId) => {
    if (brandId === data.active_brand_id) { setOpen(false); return; }
    setSwitching(true);
    try {
      await axios.post(`${BACKEND_URL}/api/brands/${brandId}/activate`, {}, { withCredentials: true });
      setData((prev) => ({ ...prev, active_brand_id: brandId }));
      setOpen(false);
      toast.success(t('brands.sw_changed'), { duration: 2000 });
      // Signal all pages to refetch their data
      window.dispatchEvent(new CustomEvent('brand:changed', { detail: { brand_id: brandId } }));
    } catch {
      toast.error(t('brands.sw_switch_fail'));
    } finally {
      setSwitching(false);
    }
  }, [data.active_brand_id, t]);

  const active = data.brands.find((b) => b.brand_id === data.active_brand_id) || data.brands[0];
  if (!active) return null;

  const canAdd = data.is_agency && data.brands.length < data.max_brands;

  return (
    <div ref={wrapRef} className="relative mb-3" data-testid="brand-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="brand-switcher-trigger"
        className="w-full flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all"
        disabled={switching}
      >
        <span
          className="w-4 h-4 rounded-full flex-shrink-0 border border-white/20"
          style={{ background: active.color || '#A855F7' }}
        />
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Workspace</div>
          <div className="text-sm font-medium truncate">{active.name}</div>
        </div>
        <CaretDown size={14} className={`text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          data-testid="brand-switcher-menu"
          className="absolute left-0 right-0 top-full mt-1 bg-[#0F0F0F] border border-white/10 rounded-lg shadow-2xl z-50 overflow-hidden"
        >
          <div className="max-h-72 overflow-y-auto py-1">
            {data.brands.map((b) => (
              <button
                key={b.brand_id}
                onClick={() => activate(b.brand_id)}
                data-testid={`brand-switcher-item-${b.brand_id}`}
                aria-current={b.brand_id === data.active_brand_id ? 'true' : undefined}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition-all"
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ background: b.color || '#A855F7' }}
                />
                <span className="flex-1 text-sm truncate">{b.name}</span>
                {b.brand_id === data.active_brand_id && <Check size={14} weight="bold" className="text-[#10B981]" />}
              </button>
            ))}
          </div>
          <div className="border-t border-white/10 py-1">
            <button
              onClick={() => { setOpen(false); navigate(canAdd ? '/settings/brands' : '/pricing'); }}
              data-testid="brand-switcher-add"
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/5 transition-all"
            >
              {canAdd ? <Plus size={14} className="text-[#A855F7]" /> : <Lock size={14} className="text-[#F59E0B]" />}
              <span className="flex-1">
                {canAdd ? t('brands.sw_add') : t('brands.sw_add_locked')}
              </span>
              <span className="text-[10px] text-white/40">{data.brands.length}/{data.max_brands}</span>
            </button>
            <button
              onClick={() => { setOpen(false); navigate('/settings/brands'); }}
              data-testid="brand-switcher-manage"
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/5 transition-all"
            >
              <Buildings size={14} className="text-white/60" />
              <span className="flex-1">{t('brands.sw_manage')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
